import * as React from 'react';
import { Route, RouteProps, useParams } from 'react-router-dom';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { HasPermissionContainer } from '@pec/aion-ui-components/containers/HasPermission';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Theme as MaterialTheme } from '@material-ui/core/styles/createMuiTheme';
import { useTypedSelector } from '../combineReducers';
import { Box, CircularProgress } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme: MaterialTheme) =>
  createStyles({
    alertWrapper: { padding: theme.spacing(0, 3) },
    alert: {
      fontWeight: theme.typography.fontWeightRegular
    },
    link: {
      fontWeight: theme.typography.fontWeightRegular,
      color: 'var(--on-primary-high-emphasis-ffffff)'
    }
  })
);

export const TestLocators = {
  link: 'ProtectedRoute-link'
};

type RouteParams = {
  organizationId: string;
};

type Props = RouteProps & {
  permissionResource: ActivityResourceName;
  activityAction?: ActivityAction;
};

export const ProtectedRoute: React.FC<Props> = ({
  component: Component,
  render,
  permissionResource,
  activityAction = ActivityAction.Read,
  ...restProps
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { organizationId } = useParams<RouteParams>();
  const { isFetching, userInfo } = useTypedSelector(state => state.userInfo);
  const fallbackLink = `/organizations/${organizationId}`;

  return (
    <HasPermissionContainer>
      {({ hasOrganizationPermission, hasGlobalPermission }) => (
        <Route
          {...restProps}
          render={props => {
            if (!userInfo || isFetching) {
              return (
                <Box display="flex" justifyContent="center" width="100%">
                  <CircularProgress />
                </Box>
              );
            } else if (
              hasGlobalPermission(activityAction, permissionResource) ||
              hasOrganizationPermission(activityAction, permissionResource)
            ) {
              if (Component) {
                return <Component {...props} />;
              }
              if (render) {
                return render(props);
              }
              return null;
            } else {
              return (
                <div className={classes.alertWrapper}>
                  <Alert elevation={6} variant="filled" severity="error" className={classes.alert}>
                    {t('subscriber:permissions.routeMessage', 'You don’t have rights to view this page.')}
                    <span>&nbsp;</span>
                    <a className={classes.link} data-testid={TestLocators.link} href={fallbackLink}>
                      {t('subscriber:permissions.routeLink', 'Please proceed here.')}
                    </a>
                  </Alert>
                </div>
              );
            }
          }}
        />
      )}
    </HasPermissionContainer>
  );
};
